import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardMedia, CardContent, CardActions,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Sell as SellIcon
} from '@mui/icons-material';
import api from '../../services/api.service';
import PageHeader from '../common/PageHeader';
import { getPhotoUrl } from '../../utils/helpers';

const fmt = (v) => Number(v || 0).toLocaleString('fr-FR') + ' FCFA';
const TYPE_LABELS = { vente: 'À vendre', location: 'À louer', don: 'À donner' };
const TYPE_COLORS = { vente: 'success', location: 'info', don: 'secondary' };

const ValidationAnnonces = () => {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dialogType, setDialogType] = useState('');
  const [raisonRejet, setRaisonRejet] = useState('');
  const [processing, setProcessing] = useState(false);

  const chargerEnAttente = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/annonces/admin/en-attente');
      setAnnonces(res.data?.data || []);
    } catch (err) {
      console.error('Erreur chargement annonces en attente:', err);
      setError('Erreur lors du chargement des annonces en attente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { chargerEnAttente(); }, []);

  const handleOpenDialog = (annonce, type) => {
    setSelected(annonce);
    setDialogType(type);
    setRaisonRejet('');
  };

  const handleCloseDialog = () => {
    setSelected(null);
    setDialogType('');
    setRaisonRejet('');
  };

  const handleValider = async () => {
    if (!selected) return;
    setProcessing(true);
    try {
      await api.put(`/annonces/${selected.id}/valider`);
      setSuccess(`Annonce "${selected.titre}" validée et publiée`);
      handleCloseDialog();
      await chargerEnAttente();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la validation');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejeter = async () => {
    if (!selected) return;
    setProcessing(true);
    try {
      await api.put(`/annonces/${selected.id}/rejeter`, { raison: raisonRejet });
      setSuccess(`Annonce "${selected.titre}" rejetée`);
      handleCloseDialog();
      await chargerEnAttente();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du rejet');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#D4AF37' }} size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Validation des Annonces"
        subtitle="Coin Affaires — approuver ou rejeter les annonces soumises par les membres"
        showLogo={false}
        showDecoration={false}
      />

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {annonces.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <CheckIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Aucune annonce en attente</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {annonces.map((a) => (
            <Grid item xs={12} sm={6} md={4} key={a.id}>
              <Card elevation={3}>
                {a.photoUrl ? (
                  <CardMedia
                    component="img"
                    image={getPhotoUrl(a.photoUrl)}
                    alt={a.titre}
                    sx={{ height: 180, objectFit: 'cover' }}
                  />
                ) : (
                  <Box sx={{ height: 180, bgcolor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SellIcon sx={{ fontSize: 48, color: '#BDBDBD' }} />
                  </Box>
                )}
                <CardContent>
                  <Chip
                    label={TYPE_LABELS[a.type] || 'À vendre'}
                    size="small"
                    color={TYPE_COLORS[a.type] || 'success'}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="subtitle1" fontWeight="bold">{a.titre}</Typography>
                  <Typography variant="body1" sx={{ color: '#D4AF37', fontWeight: 'bold' }}>{fmt(a.prix)}</Typography>
                  {a.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{a.description}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Par {a.auteur?.nom || '-'} {a.auteur?.telephone ? `(${a.auteur.telephone})` : ''}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={() => handleOpenDialog(a, 'valider')}
                    sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                  >
                    Valider
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => handleOpenDialog(a, 'rejeter')}
                  >
                    Rejeter
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={!!selected} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: dialogType === 'valider' ? '#4caf50' : '#f44336', color: 'white' }}>
          {dialogType === 'valider' ? 'Valider l\'annonce' : 'Rejeter l\'annonce'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selected && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {dialogType === 'valider'
                  ? `Publier l'annonce "${selected.titre}" ? Elle sera immédiatement visible sur la page publique Coin Affaires.`
                  : `Rejeter l'annonce "${selected.titre}" ?`}
              </Typography>

              {dialogType === 'valider' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Une notification WhatsApp sera envoyée à tous les membres actifs n'ayant pas désactivé
                  les notifications Coin Affaires.
                </Alert>
              )}

              {dialogType === 'rejeter' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Raison du rejet (optionnel)"
                  value={raisonRejet}
                  onChange={(e) => setRaisonRejet(e.target.value)}
                  placeholder="Ex: photo manquante, prix non précisé, etc."
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={processing}>Annuler</Button>
          <Button
            variant="contained"
            onClick={dialogType === 'valider' ? handleValider : handleRejeter}
            disabled={processing}
            sx={{
              bgcolor: dialogType === 'valider' ? '#4caf50' : '#f44336',
              '&:hover': { bgcolor: dialogType === 'valider' ? '#45a049' : '#da190b' }
            }}
          >
            {processing ? <CircularProgress size={24} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValidationAnnonces;
